import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Alert, IconButton, CircularProgress,
  TextField, Button, MenuItem, Checkbox, ListItemText, OutlinedInput, Select,
  InputLabel, FormControl, Chip,
} from '@mui/material';
import { Home, PlaylistAddCheck } from '@mui/icons-material';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL;

export default function BulkTextbookAssign() {
  const navigate = useNavigate();
  const [books, setBooks] = useState([]);
  const [stages, setStages] = useState([]);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    bookNos: [], mode: 'stage', stage: '', classCode: '',
    academicYear: String(new Date().getFullYear()), term: '', dueDate: '',
  });

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/api/library/books?category=Textbook`, { headers }),
      axios.get(`${API}/api/stages`, { headers }).catch(() => ({ data: [] })),
    ]).then(([b, s]) => {
      setBooks(b.data);
      setStages(Array.isArray(s.data) ? s.data : (s.data?.stages || []));
    }).catch(err => setError(err.response?.data?.error || 'Failed to load'))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line

  const submit = async () => {
    if (form.bookNos.length === 0) return setError('Select at least one textbook');
    if (form.mode === 'stage' && !form.stage) return setError('Choose a stage');
    if (form.mode === 'class' && !form.classCode.trim()) return setError('Enter a class code');
    setSubmitting(true); setResult(null);
    try {
      const payload = {
        bookNos: form.bookNos,
        academicYear: form.academicYear,
        term: form.term ? Number(form.term) : undefined,
        dueDate: form.dueDate || undefined,
      };
      if (form.mode === 'stage') payload.stage = form.stage; else payload.classCode = form.classCode;
      const { data } = await axios.post(`${API}/api/library/bulk-assign`, payload, { headers });
      setResult(data); setError('');
    } catch (err) { setError(err.response?.data?.error || 'Failed to assign'); }
    finally { setSubmitting(false); }
  };

  if (loading) return (<Box sx={{ maxWidth: 700, mx: 'auto', mt: 4, textAlign: 'center' }}><CircularProgress /></Box>);

  return (
    <Box sx={{ maxWidth: 700, mx: 'auto', mt: 4 }}>
      <Card elevation={3}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box display="flex" alignItems="center"><PlaylistAddCheck sx={{ mr: 1, color: '#512da8' }} /><Typography variant="h5" sx={{ color: '#512da8' }}>Bulk Textbook Assignment</Typography></Box>
            <IconButton onClick={() => navigate('/library')} title="Library Dashboard"><Home /></IconButton>
          </Box>
          <Typography variant="body2" color="text.secondary" mb={2}>Assign textbooks to every active learner in a stage or class at the start of the year. One available copy is picked per learner.</Typography>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <Box display="flex" flexDirection="column" gap={2}>
            <FormControl size="small" fullWidth>
              <InputLabel>Textbooks</InputLabel>
              <Select multiple value={form.bookNos} input={<OutlinedInput label="Textbooks" />}
                onChange={e => setForm({ ...form, bookNos: e.target.value })}
                renderValue={(sel) => sel.map(bn => books.find(b => b.bookNo === bn)?.title || bn).join(', ')}>
                {books.map(b => (
                  <MenuItem key={b.bookNo} value={b.bookNo}>
                    <Checkbox checked={form.bookNos.indexOf(b.bookNo) > -1} />
                    <ListItemText primary={b.title} secondary={`${b.availableCopies} available`} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField select label="Assign by" size="small" value={form.mode} onChange={e => setForm({ ...form, mode: e.target.value })}>
              <MenuItem value="stage">Stage</MenuItem>
              <MenuItem value="class">Class</MenuItem>
            </TextField>
            {form.mode === 'stage' ? (
              <TextField select label="Stage" size="small" value={form.stage} onChange={e => setForm({ ...form, stage: e.target.value })}>
                {stages.map(s => <MenuItem key={s.code} value={s.code}>{s.code} — {s.name}</MenuItem>)}
              </TextField>
            ) : (
              <TextField label="Class code" size="small" value={form.classCode} onChange={e => setForm({ ...form, classCode: e.target.value })} />
            )}
            <Box display="flex" gap={2}>
              <TextField label="Year" size="small" value={form.academicYear} onChange={e => setForm({ ...form, academicYear: e.target.value })} />
              <TextField label="Term" size="small" value={form.term} onChange={e => setForm({ ...form, term: e.target.value })} />
              <TextField label="Due date" type="date" size="small" InputLabelProps={{ shrink: true }} value={form.dueDate} onChange={e => setForm({ ...form, dueDate: e.target.value })} />
            </Box>
            <Button variant="contained" onClick={submit} disabled={submitting}>{submitting ? 'Assigning…' : 'Assign textbooks'}</Button>
          </Box>

          {result && (
            <Alert severity="success" sx={{ mt: 2 }}>
              Assigned <strong>{result.assigned}</strong> copy(ies).
              {result.skipped?.length > 0 && (
                <Box mt={1}>
                  <Typography variant="body2">Skipped {result.skipped.length}:</Typography>
                  <Box sx={{ maxHeight: 160, overflow: 'auto', mt: 0.5 }}>
                    {result.skipped.slice(0, 50).map((s, i) => (
                      <Chip key={i} size="small" sx={{ m: 0.25 }} label={`${s.admNo}/${s.bookNo}: ${s.reason}`} />
                    ))}
                  </Box>
                </Box>
              )}
            </Alert>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
